import type { StrumDirection, StrumGesture } from "../../lib/rhythm/rhythmPathPlan";
import {
  STRUM_DIRECTION_LABEL,
  STRUM_GESTURE_LABEL,
} from "../../lib/rhythm/rhythmPathPlan";

export interface StrumGestureIndicatorProps {
  /** Gesto activo; null = idle. */
  gesture: StrumGesture | null;
  /** Dirección del golpe (solo relevante en hand/palm/snap). */
  direction?: StrumDirection | null;
  size?: "sm" | "lg";
  className?: string;
  /** Mostrar chasquido en la fila (niveles superiores); si false, solo 3 gestos base. */
  showSnap?: boolean;
}

const BASE_GESTURES: StrumGesture[] = ["hand", "palm", "mute"];

function GestureIcon({
  gesture,
  direction,
  active,
  large,
}: {
  gesture: StrumGesture;
  direction: StrumDirection | null;
  active: boolean;
  large: boolean;
}) {
  const dim = large ? "h-10 w-10" : "h-7 w-7";
  const stroke = active ? "#99f6e4" : "rgba(255,255,255,0.28)";

  if (gesture === "mute") {
    return (
      <svg viewBox="0 0 40 40" className={dim} aria-hidden>
        <rect
          x="8"
          y="14"
          width="24"
          height="12"
          rx="3"
          fill="none"
          stroke={stroke}
          strokeWidth="2.2"
        />
        <path d="M12 18h16M12 22h16" stroke={stroke} strokeWidth="1.6" />
      </svg>
    );
  }

  if (gesture === "snap") {
    return (
      <svg viewBox="0 0 40 40" className={dim} aria-hidden>
        <circle cx="20" cy="20" r="11" fill="none" stroke={stroke} strokeWidth="2.2" />
        <path
          d="M20 10v8M16 16l4 4 4-4"
          fill="none"
          stroke={stroke}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  // hand / palm — flecha grande según dirección
  const isUp = direction === "up";
  return (
    <svg viewBox="0 0 40 40" className={dim} aria-hidden>
      {gesture === "palm" && (
        <rect
          x="11"
          y="8"
          width="18"
          height="24"
          rx="4"
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          opacity="0.45"
        />
      )}
      {isUp ? (
        <path
          d="M20 30V12M12 18l8-8 8 8"
          fill="none"
          stroke={stroke}
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M20 10v18M12 22l8 8 8-8"
          fill="none"
          stroke={stroke}
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/**
 * Indicador de rasgueo: gesto (mano / palma / muteo / chasquido) + flecha ↑↓.
 */
export function StrumGestureIndicator({
  gesture,
  direction = "down",
  size = "lg",
  className = "",
  showSnap = false,
}: StrumGestureIndicatorProps) {
  const isLg = size === "lg";
  const gestures = showSnap ? [...BASE_GESTURES, "snap" as const] : BASE_GESTURES;
  const active = gesture;
  const dir = direction ?? "down";

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {/* Flecha hero */}
      <div
        className={`flex flex-col items-center justify-center rounded-2xl border transition-all duration-200 ${
          isLg ? "h-28 w-28" : "h-16 w-16"
        } ${
          active === "mute"
            ? "border-amber-300/50 bg-amber-400/10"
            : active
              ? "border-[#99f6e4]/70 bg-[#99f6e4]/15 shadow-[0_0_28px_rgba(153,246,228,0.22)]"
              : "border-white/10 bg-white/[0.03]"
        }`}
        role="img"
        aria-label={
          active === "mute"
            ? "Muteo: apaga con la palma"
            : active
              ? `${STRUM_GESTURE_LABEL[active]} · ${STRUM_DIRECTION_LABEL[dir]}`
              : "Indicador de rasgueo"
        }
      >
        {active === "mute" ? (
          <GestureIcon gesture="mute" direction={null} active large={isLg} />
        ) : active ? (
          <GestureIcon
            gesture={active}
            direction={dir}
            active
            large={isLg}
          />
        ) : (
          <span className="text-white/25 text-2xl">·</span>
        )}
        <span
          className={`mt-1 font-semibold uppercase tracking-[0.12em] ${
            isLg ? "text-[11px]" : "text-[9px]"
          } ${
            active === "mute"
              ? "text-amber-200/90"
              : active
                ? "text-[#99f6e4]"
                : "text-white/30"
          }`}
        >
          {active === "mute"
            ? "Muteo"
            : active
              ? dir === "up"
                ? "↑ Arriba"
                : "↓ Abajo"
              : "—"}
        </span>
      </div>

      {/* Fila de gestos */}
      <div
        className="flex items-end justify-center gap-2 sm:gap-3"
        role="group"
        aria-label="Gestos de rasgueo"
      >
        {gestures.map((g) => {
          const on = active === g;
          return (
            <div key={g} className="flex flex-col items-center gap-1">
              <span
                className={`transition-colors ${
                  isLg
                    ? on
                      ? "text-[10px] text-[#99f6e4]"
                      : "text-[9px] text-white/25"
                    : on
                      ? "text-[8px] text-[#99f6e4]/90"
                      : "text-[8px] text-white/20"
                }`}
              >
                {STRUM_GESTURE_LABEL[g]}
              </span>
              <div
                className={`flex items-center justify-center rounded-xl border transition-all ${
                  isLg
                    ? on
                      ? "h-12 w-12 border-[#99f6e4]/60 bg-[#99f6e4]/15"
                      : "h-11 w-11 border-white/10 bg-white/[0.03]"
                    : on
                      ? "h-8 w-8 border-[#99f6e4]/50 bg-[#99f6e4]/12"
                      : "h-7 w-7 border-white/10 bg-white/[0.03]"
                }`}
                aria-current={on ? "true" : undefined}
              >
                <GestureIcon
                  gesture={g}
                  direction={g === "mute" || g === "snap" ? null : dir}
                  active={on}
                  large={false}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
